import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { ImagePlus, Layers, Plus } from 'lucide-react'

import { AddAssetDialog } from '@/components/asset-library/AddAssetDialog'
import { AssetLibraryFilterBar } from '@/components/asset-library/AssetLibraryFilterBar'
import { UnifiedAssetCard } from '@/components/asset-library/UnifiedAssetCard'
import { AssetDetailModal } from '@/components/assets/AssetDetailModal'
import { FolderManager } from '@/components/assets/FolderManager'
import { FolderTree } from '@/components/assets/FolderTree'
import { ReferenceDetailModal } from '@/components/references/ReferenceDetailModal'
import { StoryboardCard } from '@/components/storyboards/StoryboardCard'
import { StoryboardDetailModal } from '@/components/storyboards/StoryboardDetailModal'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useAllAssets } from '@/hooks/useAllAssets'
import { useStoryboards } from '@/hooks/useStoryboards'
import type { FolderType, StoryboardSummary, UnifiedAsset } from '@/types'

type TypeFilter = 'all' | 'image' | 'video' | 'reference'
// Task 4: a top-level tab toggle between the generic asset grid and the
// new Storyboards section. Default 'assets' preserves existing behaviour.
type LibraryTab = 'assets' | 'storyboards'

export function AssetLibrary() {
  const navigate = useNavigate()
  const { items, folders, loading, refresh } = useAllAssets()
  const {
    items: storyboards,
    loading: storyboardsLoading,
    refresh: refreshStoryboards,
  } = useStoryboards()

  // Tab state — top-level toggle between the generic asset grid and the
  // Storyboards section. Persistent in URL would be a nice-to-have but not
  // strictly required for v1.
  const [tab, setTab] = React.useState<LibraryTab>('assets')

  // Filter state
  const [search, setSearch] = React.useState('')
  const [typeFilter, setTypeFilter] = React.useState<TypeFilter>('all')
  const [folderFilter, setFolderFilter] = React.useState<string>('all')
  const [statusFilter, setStatusFilter] = React.useState<string>('all')
  const [starredOnly, setStarredOnly] = React.useState(false)

  // Modal state
  const [selectedItem, setSelectedItem] = React.useState<UnifiedAsset | null>(null)
  const [selectedStoryboard, setSelectedStoryboard] = React.useState<StoryboardSummary | null>(null)
  const [addDialogOpen, setAddDialogOpen] = React.useState(false)

  // Folder manager state
  const [folderManagerOpen, setFolderManagerOpen] = React.useState(false)
  const [folderManagerType, setFolderManagerType] = React.useState<FolderType>('sku')

  const hasFilters =
    search.trim() !== '' ||
    typeFilter !== 'all' ||
    folderFilter !== 'all' ||
    statusFilter !== 'all' ||
    starredOnly

  const handleClearFilters = () => {
    setSearch('')
    setTypeFilter('all')
    setFolderFilter('all')
    setStatusFilter('all')
    setStarredOnly(false)
  }

  // FolderTree.selectedId is number | null; folderFilter is string 'all' | '<id>'
  const selectedFolderId: number | null =
    folderFilter === 'all' ? null : Number(folderFilter)

  const handleFolderSelect = (id: number) => {
    // Clicking the already-selected folder deselects (returns to All)
    setFolderFilter(String(selectedFolderId) === String(id) ? 'all' : String(id))
  }

  const handleCreateFolder = (type: FolderType) => {
    setFolderManagerType(type)
    setFolderManagerOpen(true)
  }

  // Client-side filtering
  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase()

    return items.filter((item) => {
      // Search: name, aiDescription, tags
      if (q) {
        const inName = item.name.toLowerCase().includes(q)
        const inDesc = item.aiDescription?.toLowerCase().includes(q) ?? false
        const inTags = item.tags?.some((t) => t.toLowerCase().includes(q)) ?? false
        if (!inName && !inDesc && !inTags) return false
      }

      // Type filter
      if (typeFilter === 'image' && item.fileType !== 'image') return false
      if (typeFilter === 'video' && (item.fileType !== 'video' || item.sourceType === 'reference')) return false
      if (typeFilter === 'reference' && item.sourceType !== 'reference') return false

      // Folder filter
      if (folderFilter !== 'all') {
        if (item.folderId === null) return false
        if (String(item.folderId) !== folderFilter) return false
      }

      // Status filter
      if (statusFilter !== 'all' && item.analysisStatus !== statusFilter) return false

      // Starred filter — references can't be starred, so this hides them too
      if (starredOnly && !item.isStarred) return false

      return true
    })
  }, [items, search, typeFilter, folderFilter, statusFilter, starredOnly])

  const handleCardClick = (item: UnifiedAsset) => {
    setSelectedItem(item)
  }

  const handleCloseDetail = () => {
    setSelectedItem(null)
  }

  const isAsset = selectedItem?.sourceType === 'asset'
  const isReference = selectedItem?.sourceType === 'reference'

  // Storyboards tab handlers — open detail modal, route to Create Ad with
  // the primary asset id pre-attached (URL handoff already supported by
  // CreateAd.tsx).
  const handleStoryboardClick = (storyboard: StoryboardSummary) => {
    setSelectedStoryboard(storyboard)
  }
  const handleUseInCreate = (storyboard: StoryboardSummary) => {
    if (storyboard.primary_asset_id == null) return
    navigate(`/create?storyboard_asset_id=${storyboard.primary_asset_id}`)
  }
  const handleStoryboardDeleted = () => {
    refreshStoryboards()
    setSelectedStoryboard(null)
  }

  // Header copy adapts to the active tab — "37 of 42 assets" vs "12 storyboards".
  const headerSummary = (() => {
    if (tab === 'storyboards') {
      if (storyboardsLoading) return 'Loading...'
      return `${storyboards.length} storyboard${storyboards.length !== 1 ? 's' : ''}`
    }
    if (loading) return 'Loading...'
    return `${filtered.length} of ${items.length} asset${items.length !== 1 ? 's' : ''}`
  })()

  return (
    <div className="flex flex-col h-full gap-6">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
            Saved Context
          </h1>
          <p className="text-sm text-muted-foreground">{headerSummary}</p>
        </div>

        <Button
          size="sm"
          onClick={() => setAddDialogOpen(true)}
          className="shrink-0"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Asset
        </Button>
      </header>

      {/* Tab toggle — Assets / Storyboards. Storyboards tab fetches its own
          data via useStoryboards; doesn't share the asset filter bar.
          (Task 4: dedicated storyboards organisation.) */}
      <div
        className="inline-flex items-center rounded-lg border border-border bg-muted p-1 gap-1"
        role="group"
        aria-label="Library section"
      >
        <button
          type="button"
          onClick={() => setTab('assets')}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            tab === 'assets'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
          aria-pressed={tab === 'assets'}
        >
          <ImagePlus className="h-3.5 w-3.5" />
          Assets
        </button>
        <button
          type="button"
          onClick={() => setTab('storyboards')}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            tab === 'storyboards'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
          aria-pressed={tab === 'storyboards'}
        >
          <Layers className="h-3.5 w-3.5" />
          Storyboards
          {storyboards.length > 0 && (
            <span className="text-[10px] tabular-nums opacity-70">
              {storyboards.length}
            </span>
          )}
        </button>
      </div>

      {/* Filter bar — only relevant on the Assets tab. Storyboards have their
          own list endpoint and no filter UX in v1. */}
      {tab === 'assets' && (
        <AssetLibraryFilterBar
          search={search}
          onSearchChange={setSearch}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          folderFilter={folderFilter}
          onFolderFilterChange={setFolderFilter}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          starredOnly={starredOnly}
          onStarredOnlyChange={setStarredOnly}
          folders={folders}
          hasFilters={hasFilters}
          onClear={handleClearFilters}
          folderSelectClassName="md:hidden"
        />
      )}

      {/* STORYBOARDS-TAB content — single full-width grid, no sidebar. */}
      {tab === 'storyboards' && (
        <div className="min-w-0 flex-1">
          {storyboardsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-lg overflow-hidden border border-border">
                  <Skeleton className="aspect-video w-full" />
                  <div className="p-3 space-y-1.5">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : storyboards.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <Layers className="h-12 w-12 text-muted-foreground mb-4 opacity-40" />
              <h2 className="text-base font-medium mb-1">No storyboards yet</h2>
              <p className="text-sm text-muted-foreground mb-4 max-w-md">
                Generate one from the Create Image page → Storyboard tab. It will be
                auto-organised here and ready to use in Create Ad.
              </p>
              <Button size="sm" onClick={() => navigate('/create-image?tab=storyboard')}>
                <Plus className="h-4 w-4 mr-1" />
                Create Storyboard
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {storyboards.map((sb) => (
                <StoryboardCard
                  key={sb.id}
                  storyboard={sb}
                  onClick={() => handleStoryboardClick(sb)}
                  onUseInCreate={() => handleUseInCreate(sb)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ASSETS-TAB content — original grid + sidebar layout, unchanged
          structurally. Wrapped in a tab guard so it only renders on the
          Assets tab (Task 4). */}
      {tab === 'assets' && (
      <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-6 min-h-0 flex-1">
        {/* Asset grid */}
        <div className="min-w-0">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="rounded-lg overflow-hidden border border-border">
                  <Skeleton className="aspect-video w-full" />
                  <div className="p-2.5 space-y-1.5">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <ImagePlus className="h-12 w-12 text-muted-foreground mb-4 opacity-40" />
              {hasFilters ? (
                <>
                  <h2 className="text-base font-medium mb-1">No assets match your filters</h2>
                  <p className="text-sm text-muted-foreground mb-4">
                    Try adjusting your search or clearing the filters.
                  </p>
                  <Button variant="outline" size="sm" onClick={handleClearFilters}>
                    Clear filters
                  </Button>
                </>
              ) : (
                <>
                  <h2 className="text-base font-medium mb-1">No assets yet</h2>
                  <p className="text-sm text-muted-foreground mb-4">
                    Upload product images, videos, or add reference clips to get started.
                  </p>
                  <Button size="sm" onClick={() => setAddDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Asset
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filtered.map((item) => (
                <UnifiedAssetCard
                  key={item.id}
                  asset={item}
                  onClick={() => handleCardClick(item)}
                  onStarChange={() => refresh()}
                />
              ))}
            </div>
          )}
        </div>

        {/* Folder sidebar — desktop only */}
        <aside
          className="hidden md:flex flex-col border-l border-border bg-card rounded-lg overflow-hidden"
          aria-label="Folder navigation"
        >
          {/* Sidebar header */}
          <div className="px-3 pt-3 pb-2 border-b border-border shrink-0">
            <span className="text-sm font-medium">Folders</span>
          </div>

          {/* "All Assets" row */}
          <div className="px-3 pt-2 shrink-0">
            <button
              onClick={() => setFolderFilter('all')}
              className={
                folderFilter === 'all'
                  ? 'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm bg-accent text-accent-foreground text-left'
                  : 'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent/50 hover:text-foreground text-left transition-colors'
              }
              aria-pressed={folderFilter === 'all'}
            >
              <span className="flex-1">All Assets</span>
              <span className="text-xs tabular-nums text-muted-foreground">
                {items.length}
              </span>
            </button>
          </div>

          {/* FolderTree — fills remaining height. Hide the auto-created
              system 'storyboard' folder from this sidebar (Task 4 review):
              storyboards are navigated via the Storyboards tab above, and
              showing the folder here too creates a duplicate path. */}
          <div className="flex-1 min-h-0">
            <FolderTree
              folders={folders.filter((f) => f.folder_type !== 'storyboard')}
              selectedId={selectedFolderId}
              onSelect={handleFolderSelect}
              onCreateFolder={handleCreateFolder}
            />
          </div>
        </aside>
      </div>
      )}

      {/* Add Asset dialog */}
      <AddAssetDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        folders={folders}
        onAssetAdded={() => {
          refresh()
          setAddDialogOpen(false)
        }}
      />

      {/* Folder create dialog */}
      <FolderManager
        mode="create"
        folderType={folderManagerType}
        open={folderManagerOpen}
        onOpenChange={setFolderManagerOpen}
        onSuccess={() => {
          refresh()
        }}
      />

      {/* Asset detail modal */}
      <AssetDetailModal
        asset={isAsset ? (selectedItem?.originalAsset ?? null) : null}
        open={isAsset && selectedItem !== null}
        onOpenChange={(open) => { if (!open) handleCloseDetail() }}
      />

      {/* Reference detail modal */}
      <ReferenceDetailModal
        reference={isReference ? (selectedItem?.originalReference ?? null) : null}
        open={isReference && selectedItem !== null}
        onOpenChange={(open) => { if (!open) handleCloseDetail() }}
        onRefresh={refresh}
      />

      {/* Storyboard detail modal — only mounted when a storyboard is
          selected (Task 4). Lazy-fetches the full plan + linked assets. */}
      <StoryboardDetailModal
        storyboard={selectedStoryboard}
        open={selectedStoryboard !== null}
        onOpenChange={(open) => { if (!open) setSelectedStoryboard(null) }}
        onDeleted={handleStoryboardDeleted}
        onUseInCreate={handleUseInCreate}
      />
    </div>
  )
}
